<template>
  <AeCard class="card-field" :side-color="cropColor" :hoverable="clickable" @click="$emit('click')">
    <div class="cf-layout">
      <!-- SVG miniatura geometria -->

      <PlotGeoSvg
        v-if="displayGeojson"
        :geojson="displayGeojson"
        :size="44"
        :fill="fillColor"
        :stroke="cropColor"
      />

      <!-- Corpo info -->
      <div class="cf-body">
        <!-- Riga 1: nome campo -->
        <div class="cf-row-main">
          <span class="cf-name">{{ displayName }}</span>
        </div>

        <!-- Riga 2: coltura + superficie + tipo plot + piante -->
        <div class="cf-row-details">
          <span v-if="cropName" class="cf-crop">
            <span class="cf-crop-dot" :style="{ backgroundColor: cropColor }"></span>
            {{ cropName }}
          </span>
          <span v-else-if="plot?.plot_type" class="cf-crop">
             <a>{{ t('cardField.plotNoCrop') }}</a>
          </span>
          <span v-if="surface" class="cf-area">{{ formatArea(surface) }}</span>
          <span v-if="plotTypeLabel" class="cf-tag">{{ plotTypeLabel }}</span>
          <span v-if="showPlotName" class="cf-tag cf-plot-name">{{ plot?.name }}</span>
          <span v-if="plantCount" class="cf-tag">{{ plantCount }} {{ t('cardField.plants') }}</span>
          <span v-if="season" class="cf-season">{{ seasonLabel }}</span>
        </div>
      </div>

      <!-- Slot per azioni (edit/delete) -->
      <div v-if="$slots.actions" class="cf-actions" @click.stop>
        <slot name="actions" />
      </div>
    </div>
  </AeCard>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { translateCropName } from '../i18n/cropVocab'
import { AeCard } from '@aevue/components'
import PlotGeoSvg from './PlotGeoSvg.vue'

const { t } = useI18n()

const props = defineProps({
  plot: { type: Object, default: null },
  field: { type: Object, default: null },
  crop: { type: Object, default: null },
  season: { type: Object, default: null },
  plantCount: { type: Number, default: 0 },
  clickable: { type: Boolean, default: false }
})

defineEmits(['click'])

const PLOT_TYPES = computed(() => ({
  orchard: t('cardField.typeOrchard'),
  vineyard: t('cardField.typeVineyard'),
  olive_grove: t('cardField.typeOliveGrove'),
  pasture: t('cardField.typePasture')
}))

const displayName = computed(() => {
  return props.field?.name || props.plot?.name || ''
})

const displayGeojson = computed(() => {
  // Se field ha geometria custom, usa quella
  if (props.field?.geojson) {
    return props.field.geojson
  }
  // Altrimenti usa geometria del plot
  return props.plot?.geojson || null
})

const showPlotName = computed(() => {
  // Mostra nome plot se field ha un nome custom diverso
  return props.field?.name && props.field.name !== props.plot?.name && props.plot?.name
})

const cropName = computed(() => {
  if (props.crop) return translateCropName(props.crop.name || props.crop.crop_name) || null
  if (props.field?.crop) return translateCropName(props.field.crop.name || props.field.crop.crop_name) || null
  return null
})

const cropColor = computed(() => {
  const c = props.crop || props.field?.crop
  return c?.color || '#999'
})

const fillColor = computed(() => {
  const hex = cropColor.value
  // Convert hex to rgba with transparency
  const match = hex.match(/^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i)
  if (match) {
    const r = parseInt(match[1], 16)
    const g = parseInt(match[2], 16)
    const b = parseInt(match[3], 16)
    return `rgba(${r},${g},${b},0.2)`
  }
  return 'rgba(153,153,153,0.2)'
})

const surface = computed(() => {
  return props.field?.surface || props.plot?.area || props.plot?.surface || null
})

const plotTypeLabel = computed(() => {
  const pt = props.plot?.plot_type
  return pt ? (PLOT_TYPES.value[pt] || null) : null
})

const seasonLabel = computed(() => {
  const s = props.season
  if (!s) return ''
  return s.season_name || s.name || ''
})

function formatArea(m2) {
  if (!m2) return ''
  if (m2 >= 10000) return (m2 / 10000).toFixed(2) + ' ha'
  return m2.toFixed(0) + ' m²'
}
</script>

<style scoped>
.card-field {
  --cf-gap: 0.5rem;
}

.cf-layout {
  display: flex;
  align-items: center;
  gap: var(--cf-gap);
  min-height: 44px;
}

.cf-body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.cf-row-main {
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
}

.cf-name {
  font-weight: 600;
  font-size: 0.92rem;
  color: #1a202c;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 0;
}

.cf-area {
  font-size: 0.78rem;
  color: #718096;
  white-space: nowrap;
  flex-shrink: 0;
}

.cf-row-details {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  flex-wrap: wrap;
}

.cf-crop {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.8rem;
  color: #4a5568;
}

.cf-crop-dot {
 

.cf-plot-name {
  background: #e6f3ff;
  color: #2563eb;
  font-weight: 500;
} width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.cf-tag {
  font-size: 0.72rem;
  color: #718096;
  background: #edf2f7;
  border-radius: 3px;
  padding: 0 0.3rem;
  line-height: 1.5;
  white-space: nowrap;
}

.cf-season {
  font-size: 0.8rem;
  color: #a0aec0;
  margin-left: auto;
  white-space: nowrap;
}

.cf-actions {
  display: flex;
  gap: 0.15rem;
  flex-shrink: 0;
  opacity: 0;
  transition: opacity 0.15s;
}

.card-field:hover .cf-actions {
  opacity: 1;
}

/* Su touch device (no hover) i bottoni sono sempre visibili */
@media (hover: none) {
  .cf-actions {
    opacity: 1;
  }
}
</style>
